import { select, call, SagaGenerator } from "typed-redux-saga";

import {
	DataOperation,
	maybeAsyncFnWrapper,
	OverviewEngineFactories
} from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions";
import { Query } from "@com.mgmtp.a12.dataservices/dataservices-access";
import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { DocumentModel } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { EnumerationFilterOptionsView } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/filters/options-views/enumeration-filter-options-view";

import { assertTrue } from "../../utilities/assert";
import { getDocumentModelElement } from "../../utilities/modelElementHelper";
import { ModulfOverviewEngineDataLoader } from "../../app/provider/modulfOverviewEngineDataLoader";

import { PATHS } from "./constants";
import { dataModels } from "./moduleResources";

const PROCESS_FILTER_FIELDS: Record<string, string> = {
	"/Process/processMetadata/vorgangsnummer": "/root/processVorgangsnummer",
	"/Process/genehmigungsverfahren/prozessschritte/Status": "/root/processStatus"
};

function adjustCorrelatedFachverfahrenShortNameSearchOperator(constraint: Query.Operator): Query.Operator {
	if (
		constraint.operator === Query.OPERATORS.EXACT_MATCH_OPERATOR &&
		constraint.field === "/root/correlatedFachverfahrenShortName"
	) {
		return {
			...constraint,
			operator: Query.OPERATORS.SIMPLE_SEARCH_OPERATOR,
			value: String(constraint.value)
		};
	}

	if ("operands" in constraint && Array.isArray(constraint.operands)) {
		return {
			...constraint,
			operands: constraint.operands.map(adjustCorrelatedFachverfahrenShortNameSearchOperator)
		};
	}

	if ("operand" in constraint && constraint.operand) {
		return {
			...constraint,
			operand: adjustCorrelatedFachverfahrenShortNameSearchOperator(constraint.operand)
		};
	}

	return constraint;
}

function rewriteProcessFilterFields(constraint: Query.Operator): Query.Operator {
	if ("field" in constraint && typeof constraint.field === "string") {
		const rewrittenField = PROCESS_FILTER_FIELDS[constraint.field];
		if (rewrittenField) {
			return { ...constraint, field: rewrittenField };
		}
	}

	if ("fields" in constraint && Array.isArray(constraint.fields)) {
		return {
			...constraint,
			fields: constraint.fields.map(field => PROCESS_FILTER_FIELDS[field] ?? field)
		};
	}

	if ("operands" in constraint && Array.isArray(constraint.operands)) {
		return { ...constraint, operands: constraint.operands.map(rewriteProcessFilterFields) };
	}

	if ("operand" in constraint && constraint.operand) {
		return { ...constraint, operand: rewriteProcessFilterFields(constraint.operand) };
	}

	return constraint;
}

function adaptSort(sort: Query.Order[] | undefined): Query.Order[] | undefined {
	if (!sort) {
		return sort;
	}
	return sort.map(item => ({
		...item,
		field: PROCESS_FILTER_FIELDS[item.field] ?? item.field
	}));
}

export type UpcomingDeadlinesActivityData = {
	enumerationStatusOptions?: EnumerationFilterOptionsView.EnumerationOption[];
};

const STATUS_FIELD = "/Process/genehmigungsverfahren/prozessschritte/Status";
const STATUS_MODEL_PATH = [
	{ elementName: "Process" },
	{ elementName: "genehmigungsverfahren" },
	{ elementName: "prozessschritte" },
	{ elementName: "Status" }
];

type StatusOption = Pick<EnumerationFilterOptionsView.EnumerationOption, "value" | "label">;

/** One option per enum value and translation, because the index contains the localized labels. */
function getStatusOptionsFromModel(documentModel: DocumentModel): StatusOption[] {
	const statusElement = getDocumentModelElement(documentModel, STATUS_MODEL_PATH);
	if (statusElement?.type !== "Field" || statusElement.fieldType.type !== "EnumerationType") {
		return [];
	}
	return statusElement.fieldType.values.flatMap(enumValue =>
		(enumValue.label ?? []).map(label => ({ value: enumValue.value, label: label.text }))
	);
}

/** Removes the Fachverfahren marker from a status label, e.g. "Prüfung 1 [TXMZGF1_16]" -> "Prüfung 1". */
function sanitizeStatusLabel(label: string): string {
	return label.replace(/\s*\[[^\]]*]/g, "").trim();
}

/**
 * Returns the values of all status options that represent the same status as the given filter value.
 * The filter value can be an enumeration value (its label is used) or a label.
 */
function findMatchingStatusValues(filterValue: string, statusOptions: StatusOption[]): string[] {
	const optionForValue = statusOptions.find(option => option.value === filterValue);
	const searchLabel = optionForValue ? optionForValue.label : filterValue;
	if (!searchLabel) {
		return [];
	}
	const sanitizeStatusLabelValue = sanitizeStatusLabel(searchLabel);
	return [
		...new Set(
			statusOptions
				.filter(option => sanitizeStatusLabel(option.label) === sanitizeStatusLabelValue)
				.map(option => option.label)
		)
	];
}

/**
 * Expands a status exact match into an OR group of equivalent Fachverfahren-specific values.
 * Different stored enum values can share the same label in the overview.
 */
function adaptStatusOperator(constraint: Query.Operator, statusOptions: StatusOption[]): Query.Operator {
	if (constraint.operator === Query.OPERATORS.EXACT_MATCH_OPERATOR && constraint.field === STATUS_FIELD) {
		const matchingValues = findMatchingStatusValues(String(constraint.value), statusOptions);
		if (matchingValues.length === 0) {
			return constraint;
		}
		const matchingOperators = matchingValues.map(value => ({ ...constraint, value }));
		// Group equivalent codes so a parent AND does not require multiple mutually exclusive statuses.
		return matchingOperators.length === 1
			? matchingOperators[0]
			: { operator: Query.OPERATORS.OR_OPERATOR, operands: matchingOperators };
	}

	if ("operands" in constraint && Array.isArray(constraint.operands)) {
		return {
			...constraint,
			operands: constraint.operands.map(operand => adaptStatusOperator(operand, statusOptions))
		};
	}

	if ("operand" in constraint && constraint.operand) {
		return { ...constraint, operand: adaptStatusOperator(constraint.operand, statusOptions) };
	}

	return constraint;
}

function enrichConstraint(constraint: Query.Operator, statusOptions: StatusOption[]): Query.Operator {
	const updatedForFVShortNameOperator = adjustCorrelatedFachverfahrenShortNameSearchOperator(constraint);
	const adaptedConstraint = adaptStatusOperator(updatedForFVShortNameOperator, statusOptions);

	// Included process fields are enriched only after retrieval; querying their persisted copies keeps filtering in the database.
	return rewriteProcessFilterFields(adaptedConstraint);
}
/**
 * An overview engine loader that adds a fixed filter for processId to the query (so that the user can only see Wiedervorlagen
 * belonging to the current Antrag)
 */
export const wiedervorlagenWithProcessOverviewLoader: ModulfOverviewEngineDataLoader = {
	canHandle(params): boolean {
		const { queries, documentModel } = params;
		if (documentModel.header.id !== dataModels.WIEDERVORLAGE_WITH_PROCESS_D) {
			return false;
		}

		const [query] = queries;
		return DataOperation.ListDocuments.Query.isAssignableFrom(query);
	},

	*provideData(params): SagaGenerator<DataOperation.ResultSet> {
		const { activityId, queries, documentModel } = params;
		const [query, ...otherQueries] = queries;
		let updatedQuery = query;

		assertTrue(DataOperation.ListDocuments.Query.isAssignableFrom(query));
		const activityData: UpcomingDeadlinesActivityData | undefined = yield* select(
			ActivitySelectors.data(activityId)
		);
		const modelStatusOptions = getStatusOptionsFromModel(documentModel);
		// The view-written options are only a fallback; the overview engine may replace the activity data.
		const statusOptions =
			modelStatusOptions.length > 0 ? modelStatusOptions : (activityData?.enumerationStatusOptions ?? []);

		const customConstraint: Query.Operator = {
			operator: Query.OPERATORS.NOT_OPERATOR,
			operand: {
				operator: Query.OPERATORS.EXACT_MATCH_OPERATOR,
				field: PATHS.Erledigt,
				value: "true"
			}
		};

		updatedQuery = {
			...query,
			constraint: query.constraint
				? { operator: Query.OPERATORS.AND_OPERATOR, operands: [query.constraint, customConstraint] }
				: customConstraint,
			fields: [...(query?.fields ?? []), PATHS.ProcessId]
		};
		if (updatedQuery.constraint) {
			updatedQuery = {
				...updatedQuery,
				constraint: enrichConstraint(updatedQuery.constraint, statusOptions)
			};
		}
		if (updatedQuery.sort) {
			updatedQuery = {
				...updatedQuery,
				sort: adaptSort(updatedQuery.sort)
			};
		}
		return yield* call(maybeAsyncFnWrapper(OverviewEngineFactories.dataLoader.provideData), {
			...params,
			queries: [updatedQuery, ...otherQueries]
		});
	}
};
